import type { RenderSalesComponentProps, SalesComponentTypes } from 'shared/types/Render';

import { memo, useMemo, useState, useEffect } from 'react';

import { extractComponentKey } from 'shared/helpers/website';

import { Footer, type FooterProps } from './Footer';
import { Download, type DownloadProps } from './Common/Download';
import { OrderProcessing } from './OrderProcessing';

// Multiple versions
import AboutMe, { type AboutMeProps } from './AboutMe';
import CallToAction, { type CallToActionProps } from './CallToAction';
import CheckSectionWithPages, { type CheckSectionWithPagesProps } from './CheckSectionWithPages';
import FAQ, { type FAQProps } from './FAQ';
import HeroBasic, { type HeroBasicProps } from './HeroBasic';
import HeroDetailed, { type HeroDetailedProps } from './HeroDetailed';
import HowToStart, { type HowToStartProps } from './HowToStart';
import HeadlineOnly, { type HeadlineOnlyProps } from './HeadlineOnly';
import Paragraph, { type ParagraphProps } from './Paragraph';
import ThisIsForYou, { type ThisIsForYouProps } from './ThisIsForYou';
import HeadingWithButton, { type HeadingWithButtonProps } from './HeadingWithButton';
import Guarantee, { type GuaranteeProps } from './Guarantee';
import Receipt, { type ReceiptProps } from './Receipt';
import Heading, { type HeadingProps } from './Heading';
import Form, { type FormProps } from './Form';
import RecurringForm from './RecurringForm';
import UpsellVIPForm from './UpsellVIPForm';
import HeroCoach, { type HeroCoachProps } from './HeroCoach';
import MiniHero, { type MiniHeroProps } from './MiniHero';
import Video, { type VideoProps } from './Video';
import Topbar, { type TopBarTypes } from './Topbar';
import Photo, { type PhotoProps } from './Photo';
import Event, { type EventProps } from './Events';
import CustomElement from './CustomElement';
import HeroImages from './HeroImages';
import { FlexBox } from '../Common';
import {
  keyToColorMapping,
  fadedBackgroundColor
  // getOppositeColorKey
} from 'shared/helpers/pallete';

import { useBranding } from 'shared/hooks/index';

// DO NOT CHANGE THIS VERSIONING OF HEROES,
// IT IS MAPPED TO THE HERO VERSIONS IN THE BACKEND
// SO IF YOU CHANGE IT EXISTING SALES PAGE WILL RENDER DIFFERENT COMPONENT
const Heroes = {
  V1: HeroBasic.V1,
  V2: HeroBasic.V2,
  V3: HeroBasic.V3,
  V4: HeroBasic.V4,
  V5: HeroCoach.V1,
  V6: HeroCoach.V2,
  V7: HeroBasic.V7,
  V8: HeroBasic.V8,
  V9: HeroBasic.V5,
  V10: HeroBasic.V6,
  V11: HeroDetailed.V1,
  V12: HeroDetailed.V2,
  V13: HeroDetailed.V3,
  V14: HeroDetailed.V4,
  V15: HeroDetailed.V5,
  V16: HeroDetailed.V6,
  V17: HeroDetailed.V7,
  V18: HeroDetailed.V8,
  V19: HeroImages.V1,
  V20: HeroImages.V2,
  V21: HeroImages.V3,
  V22: HeroImages.V4,
  V23: HeroImages.V5,
  V24: HeroImages.V6,
  V25: HeroImages.V7,
  V26: HeroImages.V8,
  V27: HeroImages.V9
};

const Components: SalesComponentTypes = {
  topbar: (version) => Topbar[version],
  hero_detailed: (version) => HeroDetailed[version],
  hero_coach: (version) => HeroCoach[version],
  hero: (version) => Heroes[version],
  heading: (version) => Heading[version],
  check_section: (version) => CheckSectionWithPages[version],
  for_you: (version) => ThisIsForYou[version],
  about_me: (version) => AboutMe[version],
  call_to_action: (version) => CallToAction[version],
  faq: (version) => FAQ[version],
  guarantee: (version) => Guarantee[version],
  heading_with_button: (version) => HeadingWithButton[version],
  heading_with_button_community: (version) => HeadingWithButton[version],
  heading_with_button_event: (version) => HeadingWithButton[version],
  heading_with_button_calendar: (version) => HeadingWithButton[version],
  heading_with_button_upsell: (version) => HeadingWithButton[version],
  headline_only: (version) => HeadlineOnly[version],
  hero_basic: (version) => HeroBasic[version],
  hero_images: (version) => HeroImages[version],
  how_to_start: (version) => HowToStart[version],
  paragraph: (version) => Paragraph[version],
  mini_hero: (version) => MiniHero[version],
  video: (version) => Video[version],
  photo: (version) => Photo[version],
  form: (version) => Form[version],
  recurring_form: (version) => RecurringForm[version],
  upsell_vip_form: (version) => UpsellVIPForm[version],
  receipt: () => Receipt.V1,
  event: (version) => Event[version],
  custom_element: () => CustomElement,
  custom_element_tool: () => CustomElement
};

const SalesRenderer = memo<RenderSalesComponentProps>(
  ({
    accessor,
    props,
    cover_thumbnail,
    mockup,
    pageImages,
    textColor,
    backgroundColor,
    secondaryColor,
    navigate,
    stripe,
    type,
    sub_domain,
    deliveryToken,
    book_name,
    book_price,
    currency,
    subscriptionData,
    colorPalette,
    font,
    customBackgroundColor,
    oppositeBackgroundColor
  }) => {
    SalesRenderer.displayName = `SalesComponent_ ${accessor}`;

    const { colorPalette: colorPaletteBranding, colorPaletteCollection } = useBranding();
    const [currentColorPalette, setCurrentColorPalette] = useState(
      colorPalette || colorPaletteBranding
    );

    useEffect(() => {
      const foundColor = colorPaletteCollection.find(
        (color) =>
          color === colorPaletteBranding &&
          Object.values(color).some(
            (value) =>
              typeof value === 'string' &&
              backgroundColor &&
              value.trim().toLowerCase().includes(backgroundColor.trim().toLowerCase())
          )
      );

      setCurrentColorPalette(foundColor || currentColorPalette);
    }, [
      colorPalette,
      colorPaletteBranding,
      currentColorPalette,
      backgroundColor,
      colorPaletteCollection
    ]);

    const componentKey = useMemo(() => {
      return extractComponentKey(accessor as keyof SalesComponentTypes);
    }, [accessor]);

    if (componentKey in Components) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const Comp = Components[componentKey as keyof SalesComponentTypes](props.version);
      if (!Comp) return null;

      if (props.meta?.['background_color'] && currentColorPalette) {
        const metaName = props.meta['background_color'];
        const isFaded = metaName.includes('faded_color');

        const primaryColor = isFaded
          ? fadedBackgroundColor(metaName, currentColorPalette)
          : keyToColorMapping(metaName, currentColorPalette);

        // const oppositeKey = getOppositeColorKey(metaName);

        // const oppositeColor = oppositeKey?.startsWith('faded_color_')
        //   ? fadedBackgroundColor(oppositeKey, currentColorPalette)
        //   : oppositeKey?.startsWith('color')
        //     ? keyToColorMapping(oppositeKey, currentColorPalette)
        //     : oppositeKey || '#C8C8C8';

        customBackgroundColor = primaryColor;
        // oppositeBackgroundColor = oppositeColor;
      }

      return (
        <FlexBox flexDirection="column">
          {localStorage.getItem('salesDevMode') === '1' ? (
            <h1>
              {componentKey} - {props.version}
            </h1>
          ) : null}
          <Comp
            id={accessor}
            cover_thumbnail={cover_thumbnail}
            mockup={mockup}
            textColor={textColor}
            backgroundColor={backgroundColor}
            secondaryColor={secondaryColor}
            pageImages={pageImages}
            type={type}
            sub_domain={sub_domain}
            deliveryToken={deliveryToken}
            navigate={navigate}
            stripe={stripe}
            book_name={book_name}
            book_price={book_price}
            subscriptionData={subscriptionData}
            currency={currency}
            {...props}
            meta={{
              ...props.meta,
              accessorKey: accessor
            }}
            key={accessor}
            colorPalette={colorPalette}
            font={font}
            customBackgroundColor={customBackgroundColor}
            oppositeBackgroundColor={oppositeBackgroundColor}
          />
        </FlexBox>
      );
    }
    return null;
  }
);

export type {
  TopBarTypes,
  FooterProps,
  DownloadProps,
  CallToActionProps,
  AboutMeProps,
  SalesComponentTypes,
  CheckSectionWithPagesProps,
  HeroBasicProps,
  HeroDetailedProps,
  HowToStartProps,
  HeadlineOnlyProps,
  ParagraphProps,
  ThisIsForYouProps,
  HeadingWithButtonProps,
  GuaranteeProps,
  FAQProps,
  ReceiptProps,
  HeadingProps,
  FormProps,
  HeroCoachProps,
  MiniHeroProps,
  VideoProps,
  PhotoProps,
  EventProps
};

export {
  Topbar,
  Footer,
  Download,
  OrderProcessing,
  // Multiple Versions
  AboutMe,
  CallToAction,
  CheckSectionWithPages,
  HeroBasic,
  HeroDetailed,
  HowToStart,
  HeadlineOnly,
  Paragraph,
  ThisIsForYou,
  HeadingWithButton,
  Guarantee,
  Heading,
  Receipt
};

export default SalesRenderer;
